import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../components/footer";
import Header from "../components/header";
import SeoLinksSection from "../components/SeoLinksSection";
import { API_URL } from "./utils/utils";

function generateTimeOptions() {
  const options = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute of [0, 15, 30, 45]) {
      const h = String(hour).padStart(2, "0");
      const m = String(minute).padStart(2, "0");
      options.push(`${h}:${m}`);
    }
  }
  return options;
}

export default function Home5() {
  const navigate = useNavigate();
  const [tripType, setTripType] = useState("oneway");
  const [showPassengerPopup, setShowPassengerPopup] = useState(false);
  const [showPassengerModal, setShowPassengerModal] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [validationErrorType, setValidationErrorType] = useState(null);
  const [formData, setFormData] = useState({
    pickUp: "",
    dropOff: "",
    pickUpLat: "",
    pickUpLng: "",
    dropOffLat: "",
    dropOffLng: "",
    flightArrival: "",
    returnDate: "",
    pickUpIsAirport: false,
    dropOffIsAirport: false,
  });
  const [passengers, setPassengers] = useState({
    adults: 1,
    children: 0,
    infants: 0,
  });
  const [luggage, setLuggage] = useState({
    large: 0,
    medium: 0,
  });
  const [carSeat, setCarSeat] = useState(0);
  const [showPickupDateTimePicker, setShowPickupDateTimePicker] = useState(false);
  const [showReturnDateTimePicker, setShowReturnDateTimePicker] = useState(false);
  const [pickupTempDate, setPickupTempDate] = useState("");
  const [pickupTempTime, setPickupTempTime] = useState("");
  const [returnTempDate, setReturnTempDate] = useState("");
  const [returnTempTime, setReturnTempTime] = useState("");

  // Google Maps Places Autocomplete
  const [googleApiKey, setGoogleApiKey] = useState(null);
  const [googleLoaded, setGoogleLoaded] = useState(false);
  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const debounceRef = useRef(null);
  const pickUpInputRef = useRef(null);
  const dropOffInputRef = useRef(null);
  const flightArrivalInputRef = useRef(null);
  const returnDateInputRef = useRef(null);
  const [pickUpSuggestions, setPickUpSuggestions] = useState([]);
  const [dropOffSuggestions, setDropOffSuggestions] = useState([]);
  const [pickUpDropdownRect, setPickUpDropdownRect] = useState(null);
  const [dropOffDropdownRect, setDropOffDropdownRect] = useState(null);
  const [swapBtnHover, setSwapBtnHover] = useState(false);
  const shopSliderRef = useRef(null);

  const airportRoutes = [
    { id: 1, from: "Heathrow", to: "Central London", img: "Heathrow_Central_London.png", fromPrice: "45" },
    { id: 2, from: "Gatwick", to: "Brighton", img: "Gatwick_Brighton.png", fromPrice: "55" },
    { id: 3, from: "Stansted", to: "London", img: "Stansted_London.png", fromPrice: "65" },
    { id: 4, from: "Luton", to: "Central London", img: "Luton_Central_London.png", fromPrice: "50" },
    { id: 5, from: "Heathrow", to: "Gatwick", img: "Heathrow_Gatwick.png", fromPrice: "85" },
    { id: 6, from: "London City Airport", to: "Canary Wharf", img: "London_City_Airport_Canary_Wharf.png", fromPrice: "35" },
  ];

  // Initialize shop (vehicle) slider when component mounts; Slick is loaded by main.js only on document.ready, so when navigating to this page the slider is never initialized without this.
  useEffect(() => {
    const initSlider = () => {
      if (!shopSliderRef.current) return false;
      const $ = window.jQuery;
      if (!$ || !$.fn.slick) return false;
      const $el = $(shopSliderRef.current);
      if (!$el.length) return false;
      if ($el.hasClass("slick-initialized")) $el.slick("unslick");
      $el.slick({
        infinite: true,
        slidesToShow: 4,
        slidesToScroll: 1,
        arrows: false,
        dots: false,
        autoplay: true,
        autoplaySpeed: 2000,
        speed: 2000,
        rows: 0,
        pauseOnHover: true,
        pauseOnFocus: true,
        responsive: [
          { breakpoint: 1000, settings: { slidesToShow: 2 } },
          { breakpoint: 800, settings: { slidesToShow: 1 } },
        ],
      });
      return true;
    };
    const timeouts = [];
    const clearTimeouts = () => timeouts.forEach((id) => clearTimeout(id));
    [150, 500, 1200].forEach((ms) => {
      timeouts.push(setTimeout(() => {
        if (initSlider()) clearTimeouts();
      }, ms));
    });
    return () => {
      clearTimeouts();
      const $ = window.jQuery;
      if ($ && $.fn.slick && shopSliderRef.current) {
        const $el = $(shopSliderRef.current);
        if ($el.hasClass("slick-initialized")) $el.slick("unslick");
      }
    };
  }, []);

  // Measure dropdown position for portal (so it appears above white-overlay)
  const measureDropdown = (field) => {
    const el = field === "pickUp" ? pickUpInputRef.current : dropOffInputRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const style = {
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    };
    if (field === "pickUp") setPickUpDropdownRect(style);
    else setDropOffDropdownRect(style);
  };

  useEffect(() => {
    if (pickUpSuggestions.length > 0) measureDropdown("pickUp");
    else setPickUpDropdownRect(null);
  }, [pickUpSuggestions]);

  useEffect(() => {
    if (dropOffSuggestions.length > 0) measureDropdown("dropOff");
    else setDropOffDropdownRect(null);
  }, [dropOffSuggestions]);

  useEffect(() => {
    if (pickUpSuggestions.length === 0 && dropOffSuggestions.length === 0) return;
    const onScrollOrResize = () => {
      if (pickUpSuggestions.length > 0) measureDropdown("pickUp");
      if (dropOffSuggestions.length > 0) measureDropdown("dropOff");
    };
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [pickUpSuggestions.length, dropOffSuggestions.length]);

  // Cleanup popup on unmount
  useEffect(() => {
    return () => {
      setShowPassengerPopup(false);
    };
  }, []);

  // Fetch Google Maps API key from backend API
  useEffect(() => {
    const fetchGoogleApiKey = async () => {
      try {
        // Adjust this endpoint to match your backend
        const response = await fetch(`${API_URL}get_map_key/1`);
        if (!response.ok) {
          throw new Error("Failed to fetch Google Maps API key");
        }
        const data = await response.json();
        if (data && data.data.google_map_api_key) {
          setGoogleApiKey(data.data.google_map_api_key);
        }
      } catch (error) {
        console.error("Error fetching Google Maps API key:", error);
      }
    };

    fetchGoogleApiKey();
  }, []);

  // Load Google Maps script once we have the API key
  useEffect(() => {
    if (!googleApiKey) return;

    if (window.google && window.google.maps && window.google.maps.places) {
      setGoogleLoaded(true);
      return;
    }

    const existingScript = document.getElementById("google-maps-script");
    if (existingScript) {
      existingScript.onload = () => setGoogleLoaded(true);
      return;
    }

    const script = document.createElement("script");
    script.id = "google-maps-script";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${googleApiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => setGoogleLoaded(true);
    document.body.appendChild(script);
  }, [googleApiKey]);

  const ensureAutocompleteService = () => {
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      return null;
    }
    if (!autocompleteServiceRef.current) {
      autocompleteServiceRef.current =
        new window.google.maps.places.AutocompleteService();
    }
    return autocompleteServiceRef.current;
  };

  const ensurePlacesService = () => {
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      return null;
    }
    if (!placesServiceRef.current) {
      // We don't need an actual map instance, a dummy div works
      const dummyMapDiv = document.createElement("div");
      placesServiceRef.current = new window.google.maps.places.PlacesService(
        dummyMapDiv
      );
    }
    return placesServiceRef.current;
  };

  const handlePassengerChange = (type, action) => {
    setPassengers((prev) => {
      const newValue = { ...prev };
      if (action === "increment") {
        newValue[type]++;
      } else if (action === "decrement" && newValue[type] > 0) {
        if (type === "adults" && newValue[type] > 1) {
          newValue[type]--;
        } else if (type !== "adults") {
          newValue[type]--;
        }
      }
      return newValue;
    });
  };

  const getTotalPassengers = () => {
    return passengers.adults + passengers.children + passengers.infants;
  };

  const getPassengerDisplay = () => {
    const parts = [];
    if (passengers.adults > 0) {
      parts.push(`${passengers.adults} Adult${passengers.adults > 1 ? "s" : ""}`);
    }
    if (passengers.children > 0) {
      parts.push(`${passengers.children} Child${passengers.children > 1 ? "ren" : ""}`);
    }
    if (passengers.infants > 0) {
      parts.push(`${passengers.infants} Infant${passengers.infants > 1 ? "s" : ""}`);
    }
    return parts.length > 0 ? parts.join(", ") : "Select Passengers";
  };

  const handleLocationChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Only run autocomplete for pickUp / dropOff when Google is loaded
    if (!googleLoaded || !value) {
      if (field === "pickUp") setPickUpSuggestions([]);
      if (field === "dropOff") setDropOffSuggestions([]);
      return;
    }

    const service = ensureAutocompleteService();
    if (!service) return;

    // Debounce requests
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      // No types filter = all place types (addresses + airports + establishments)
      service.getPlacePredictions(
        {
          input: value,
          componentRestrictions: { country: "gb" }, // Restrict to UK
        },
        (predictions, status) => {
          if (
            status !==
              window.google.maps.places.PlacesServiceStatus.OK ||
            !predictions
          ) {
            if (field === "pickUp") setPickUpSuggestions([]);
            if (field === "dropOff") setDropOffSuggestions([]);
            return;
          }

          const items = predictions.map((p) => {
            const types = p.types || [];
            const sf = p.structured_formatting || {};
            const mainText = sf.main_text || p.description || "";
            const secondaryText = sf.secondary_text || "";
            const desc = p.description || "";
            const isAirport = types.includes("airport") || /airport(?!\s+(terminal|term))/i.test(desc);
            const isTerminal = types.includes("transit_station") || /terminal|term\s*\d/i.test(desc);
            let category = "Address";
            if (isAirport && !isTerminal) category = "Airport";
            else if (isTerminal) category = "Terminal";
            else if (types.some((t) => ["establishment", "point_of_interest"].includes(t))) category = "Place";

            return {
              description: p.description,
              placeId: p.place_id,
              mainText,
              secondaryText,
              types,
              category,
            };
          });

          if (field === "pickUp") setPickUpSuggestions(items);
          if (field === "dropOff") setDropOffSuggestions(items);
        }
      );
    }, 300);
  };

  // Group suggestions by category for styled dropdown (Airport, Terminal, Place, Address)
  const groupSuggestionsByCategory = (suggestions) => {
    const order = ["Airport", "Terminal", "Place", "Address"];
    const groups = {};
    order.forEach((cat) => (groups[cat] = []));
    suggestions.forEach((s) => {
      const cat = s.category || "Address";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(s);
    });
    return order.filter((cat) => groups[cat]?.length > 0).map((cat) => ({ category: cat, items: groups[cat] }));
  };

  const handleSelectSuggestion = (field, suggestion) => {
    const placesService = ensurePlacesService();
    if (!placesService) {
      // Fallback: just set the text without coordinates
      const maybeAirport = /airport|terminal/i.test(suggestion.description);
      setFormData((prev) => ({
        ...prev,
        [field]: suggestion.description,
        ...(field === "pickUp"
          ? { pickUpIsAirport: maybeAirport }
          : { dropOffIsAirport: maybeAirport }),
      }));
      if (field === "pickUp") setPickUpSuggestions([]);
      if (field === "dropOff") setDropOffSuggestions([]);
      return;
    }

    placesService.getDetails(
      {
        placeId: suggestion.placeId,
        fields: ["geometry", "formatted_address", "types", "name"],
      },
      (place, status) => {
        if (
          status !== window.google.maps.places.PlacesServiceStatus.OK ||
          !place ||
          !place.geometry ||
          !place.geometry.location
        ) {
          // If details fail, still set the description and best-guess airport flag
          const maybeAirport = /airport|terminal/i.test(suggestion.description);
          setFormData((prev) => ({
            ...prev,
            [field]: suggestion.description,
            ...(field === "pickUp"
              ? { pickUpIsAirport: maybeAirport }
              : { dropOffIsAirport: maybeAirport }),
          }));
        } else {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          const address = place.formatted_address || suggestion.description;
          const types = place.types || [];
          const name = place.name || "";
          const isAirport =
            types.includes("airport") ||
            /airport|terminal/i.test(address) ||
            /airport|terminal/i.test(name);

          setFormData((prev) => ({
            ...prev,
            [field]: address,
            ...(field === "pickUp"
              ? {
                  pickUpLat: String(lat),
                  pickUpLng: String(lng),
                  pickUpIsAirport: isAirport,
                }
              : {
                  dropOffLat: String(lat),
                  dropOffLng: String(lng),
                  dropOffIsAirport: isAirport,
                }),
          }));
        }

        if (field === "pickUp") setPickUpSuggestions([]);
        if (field === "dropOff") setDropOffSuggestions([]);
      }
    );
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // For pickUp / dropOff, also trigger Places suggestions
    if (name === "pickUp" || name === "dropOff") {
      handleLocationChange(name, value);
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSwapLocations = () => {
    setFormData((prev) => ({
      ...prev,
      pickUp: prev.dropOff,
      dropOff: prev.pickUp,
      pickUpLat: prev.dropOffLat,
      pickUpLng: prev.dropOffLng,
      dropOffLat: prev.pickUpLat,
      dropOffLng: prev.pickUpLng,
      pickUpIsAirport: prev.dropOffIsAirport,
      dropOffIsAirport: prev.pickUpIsAirport,
    }));
    setPickUpSuggestions([]);
    setDropOffSuggestions([]);
  };

  const handleClearLocation = (field) => {
    if (field === "pickUp") {
      setFormData((prev) => ({
        ...prev,
        pickUp: "",
        pickUpLat: "",
        pickUpLng: "",
        pickUpIsAirport: false,
      }));
      setPickUpSuggestions([]);
    } else if (field === "dropOff") {
      setFormData((prev) => ({
        ...prev,
        dropOff: "",
        dropOffLat: "",
        dropOffLng: "",
        dropOffIsAirport: false,
      }));
      setDropOffSuggestions([]);
    }
  };

  // Helpers to manage date + time as a single ISO-like string (YYYY-MM-DDTHH:mm)
  const handleDateTimeChange = (field, date, time) => {
    setFormData((prev) => {
      const combined = date && time ? `${date}T${time}` : "";
      return {
        ...prev,
        [field]: combined,
      };
    });
  };

  const getDatePart = (value) => {
    if (!value) return "";
    const [date] = value.split("T");
    return date || "";
  };

  const getTimePart = (value) => {
    if (!value) return "";
    const [, time] = value.split("T");
    return time || "";
  };

  const formatDisplayDateTime = (value) => {
    if (!value) return "";
    const [date, time] = value.split("T");
    if (!date) return "";
    const [year, month, day] = date.split("-");
    const datePart = `${day}/${month}/${year}`;
    return time ? `${datePart} ${time}` : datePart;
  };

  const buildDateFromParts = (date, time) => {
    if (!date || !time) return null;
    const combined = `${date}T${time}`;
    const d = new Date(combined);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const getTripLabelStyle = (isActive) => ({
    cursor: "pointer",
    fontSize: "16px",
    padding: "6px 18px",
    borderRadius: "999px",
    border: "1px solid #d70015",
    backgroundColor: isActive ? "#d70015" : "#ffffff",
    color: isActive ? "#ffffff" : "#333333",
    boxShadow: isActive ? "0 0 0 2px rgba(255, 7, 7, 0.35)" : "none",
    transition: "all 0.15s ease-in-out",
  });

  const handleFormSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log("Form submit triggered", formData);
    
    // Close popup if open before navigation
    if (showPassengerPopup) {
      setShowPassengerPopup(false);
    }

    // Clear previous validation errors
    setValidationError("");
    setValidationErrorType(null);

    // Validate required fields
    if (!formData.pickUp || !formData.dropOff || !formData.flightArrival) {
      setValidationError("Please fill in all required fields.");
      setValidationErrorType("required");
      return;
    }

    // Validate pickup not in the past
    if (formData.flightArrival) {
      const dep = new Date(formData.flightArrival).getTime();
      const now = Date.now();
      if (!Number.isNaN(dep) && dep < now) {
        setValidationError("Pickup date/time cannot be in the past.");
        setValidationErrorType("dates");
        return;
      }
    }

    // Validate return date for Round trip
    if (tripType === "Round" && !formData.returnDate) {
      setValidationError("Return date is required for round trip.");
      setValidationErrorType("required");
      return;
    }

    // Prevent return being same as or before pickup when both are set
    if (formData.flightArrival && formData.returnDate) {
      const dep = new Date(formData.flightArrival).getTime();
      const ret = new Date(formData.returnDate).getTime();
      if (!Number.isNaN(dep) && !Number.isNaN(ret) && ret <= dep) {
        setValidationError(
          "Return date/time must be after pickup date/time."
        );
        setValidationErrorType("dates");
        return;
      }
    }

    // Enforce airport rule: at least one of pickUp / dropOff must be airport/terminal
    const pickUpLooksAirport =
      formData.pickUpIsAirport || /airport|terminal/i.test(formData.pickUp || "");
    const dropOffLooksAirport =
      formData.dropOffIsAirport || /airport|terminal/i.test(formData.dropOff || "");

    if (!pickUpLooksAirport && !dropOffLooksAirport) {
      setValidationError(
        "Airport transfers only: either Pick Up or Drop Off must be an airport or terminal."
      );
      setValidationErrorType("airport");
      return;
    }
    
    console.log("Validation passed, showing passenger modal");
    
    // Show modal for passenger and luggage selection
    setShowPassengerModal(true);
  };

  const handlePassengerModalSubmit = () => {
    // Close modal and navigate to booking page
    setShowPassengerModal(false);
    
    const searchParams = new URLSearchParams({
      tripType,
      pickUp: formData.pickUp,
      dropOff: formData.dropOff,
      pickUpLat: formData.pickUpLat || "",
      pickUpLng: formData.pickUpLng || "",
      dropOffLat: formData.dropOffLat || "",
      dropOffLng: formData.dropOffLng || "",
      flightArrival: formData.flightArrival,
      returnDate: formData.returnDate || "",
      adults: passengers.adults,
      children: passengers.children,
      infants: passengers.infants,
      largeLuggage: luggage.large,
      mediumLuggage: luggage.medium,
      carSeat: carSeat,
    });
    
    navigate(`/new_booking?${searchParams.toString()}`);
  };

  const handleLuggageChange = (type, action) => {
    setLuggage((prev) => {
      const newValue = { ...prev };
      if (action === "increment") {
        newValue[type]++;
      } else if (action === "decrement" && newValue[type] > 0) {
        newValue[type]--;
      }
      return newValue;
    });
  };

  const handleCarSeatChange = (action) => {
    setCarSeat((prev) => {
      if (action === "increment") return prev + 1;
      if (action === "decrement" && prev > 0) return prev - 1;
      return prev;
    });
  };

  const handleCloseValidation = () => {
    if (validationErrorType === "airport") {
      setFormData((prev) => ({
        ...prev,
        dropOff: "",
        dropOffLat: "",
        dropOffLng: "",
        dropOffIsAirport: false,
      }));
      setDropOffSuggestions([]);
    }
    setValidationError("");
    setValidationErrorType(null);
  };

  return (
    <>
      {validationError && (
        <div
          className="custom-alert-overlay"
          onClick={handleCloseValidation}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.45)",
            zIndex: 2300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            className="custom-alert box-shadow bg-white rounded p-4"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: "420px",
              width: "90%",
              textAlign: "center",
            }}
          >
            <div
              className="mb-3 d-flex justify-content-center align-items-center"
            >
              <span
                style={{
                  display: "inline-flex",
                  width: "56px",
                  height: "56px",
                  borderRadius: "50%",
                  backgroundColor: "rgba(255,193,7,0.15)",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <i
                  className="fa fa-exclamation-triangle"
                  style={{ color: "#ffc107", fontSize: "28px" }}
                />
              </span>
            </div>
            <h4 className="mb-2">Warning</h4>
            <p className="mb-4" style={{ fontSize: "14px" }}>
              {validationError}
            </p>
            <button
              type="button"
              className="nir-btn w-100"
              onClick={handleCloseValidation}
            >
              OK
            </button>
          </div>
        </div>
      )}
      <Header />
      <>
        {/* banner starts */}
        <section
          className="banner pb-10 overflow-hidden"
          style={{ backgroundImage: "url(images/section-bg1.png)" }}
        >
          <div className="container" style={{ maxWidth: "1200px" }}>
            <div className="banner-in">
              <div className="row align-items-center">
                <div className="col-lg-12 mb-4">
                  <div className="banner-content w-100 mx-auto">
                    <div className="banner-content-in text-center">
                      <h4 className="mb-0">
                           Airport Transfers Made Effortless
                      </h4>
                      <h2 className="theme mb-1">
                          Your flight. Your ride. Perfectly timed.
                      </h2>

                    </div>
                    <div className="book-form1 d-flex justify-content-center">
                      <div className="bg-white mt-4 mb-2 rounded p-4 book-form1-inner" style={{ border: "1px solid #d70015", maxWidth: "100%", boxSizing: "border-box", paddingLeft: "calc(1rem + 10px)", paddingRight: "calc(1rem + 10px)" }}>

                        {/* Trip Type Radio Buttons */}
                        <div className="row">
                          <div className="col-12 d-flex justify-content-center align-items-center">
                            <div className="d-flex align-items-center gap-2">
                              <div className="form-check" style={{ paddingLeft: 0 }}>
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="tripType"
                                  id="oneway"
                                  value="oneway"
                                  checked={tripType === "oneway"}
                                  onChange={() => {
                                    setTripType("oneway");
                                    setFormData((prev) => ({
                                      ...prev,
                                      returnDate: "",
                                    }));
                                    setPickUpSuggestions([]);
                                    setDropOffSuggestions([]);
                                  }}
                                  style={{
                                    position: "absolute",
                                    opacity: 0,
                                    pointerEvents: "none",
                                  }}
                                />
                                <label
                                  className="form-check-label fw-bold ms-0 me-2"
                                  htmlFor="oneway"
                                  style={getTripLabelStyle(tripType === "oneway")}
                                >
                                  One Way
                                </label>
                              </div>
                              <div className="form-check" style={{ paddingLeft: 0 }}>
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="tripType"
                                  id="roundtrip"
                                  value="Round"
                                  checked={tripType === "Round"}
                                  onChange={() => {
                                    setTripType("Round");
                                    setPickUpSuggestions([]);
                                    setDropOffSuggestions([]);
                                  }}
                                  style={{
                                    position: "absolute",
                                    opacity: 0,
                                    pointerEvents: "none",
                                  }}
                                />
                                <label
                                  className="form-check-label fw-bold ms-0"
                                  htmlFor="roundtrip"
                                  style={getTripLabelStyle(tripType === "Round")}
                                >
                                  Round Trip
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Form Fields */}
                        <form
                          onSubmit={(e) => {
                            console.log("Form onSubmit event fired");
                            handleFormSubmit(e);
                          }}
                          noValidate
                        >
                          <div className="row g-2 align-items-center justify-content-center flex-nowrap book-form1-row" style={{ boxSizing: "border-box", paddingTop: "10px" }}>
                            {/* Pick Up - narrower when Round Trip to fit Return Date */}
                            <div className="col-md-6 book-form1-col" style={{ flex: tripType === "Round" ? "0 0 22%" : "0 0 26%", maxWidth: tripType === "Round" ? "22%" : "26%", minWidth: 0 }}>
                              <div className="form-group position-relative mb-0" style={{ zIndex: 1050 }}>
                                <label className="theme fw-bold mb-1 d-block" style={{ fontSize: "1rem" }}>Pick Up</label>
                                <input
                                  ref={pickUpInputRef}
                                  type="text"
                                  name="pickUp"
                                  value={formData.pickUp}
                                  onChange={handleInputChange}
                                  className="form-control border"
                                  placeholder="Enter pick up location"
                                  style={{ borderRadius: "4px", padding: "7px 13px", fontSize: "1rem", height: "42px", boxSizing: "border-box", width: "100%" }}
                                  autoComplete="off"
                                  required
                                />
                                {formData.pickUp && (
                                  <button
                                    type="button"
                                    className="theme close-circle"
                                    onClick={() => handleClearLocation("pickUp")}
                                    style={{
                                      position: "absolute",
                                      right: "3px",
                                      top: "calc(60% + 10px)",
                                      transform: "translateY(-50%)",
                                      border: "none",
                                      background: "transparent",
                                      padding: 0,
                                      color: "#777",
                                      cursor: "pointer",
                                    }}
                                    aria-label="Clear pick up location"
                                  >
                                    <i
                                      className="fa fa-times-circle"
                                      style={{ fontSize: "18px" }}
                                    />
                                  </button>
                                )}
                                {googleLoaded && pickUpSuggestions.length > 0 && pickUpDropdownRect &&
                                  createPortal(
                                    <div
                                      className="places-suggestion-list places-suggestion-dropdown"
                                      style={{
                                        position: "fixed",
                                        top: pickUpDropdownRect.top,
                                        left: pickUpDropdownRect.left,
                                        width: pickUpDropdownRect.width,
                                        zIndex: 99999,
                                        maxHeight: "280px",
                                        overflowY: "auto",
                                        backgroundColor: "#fff",
                                        margin: 0,
                                        borderRadius: "8px",
                                        boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                                        border: "1px solid #e8e8e8",
                                        padding: "6px 0",
                                      }}
                                    >
                                      {groupSuggestionsByCategory(pickUpSuggestions).map(({ category, items }) => (
                                        <div key={category} className="places-suggestion-group">
                                          <div className="places-suggestion-group-header">
                                            <i className={category === "Airport" ? "fa fa-plane" : category === "Terminal" ? "fa fa-building" : category === "Place" ? "fa fa-map-marker-alt" : "fa fa-map-pin"} />
                                            <span>{category}</span>
                                          </div>
                                          {items.map((s) => (
                                            <div
                                              key={s.placeId}
                                              className="places-suggestion-item"
                                              onClick={() => handleSelectSuggestion("pickUp", s)}
                                            >
                                              <span className="places-suggestion-main">{s.mainText || s.description}</span>
                                              {s.secondaryText && <span className="places-suggestion-secondary">{s.secondaryText}</span>}
                                            </div>
                                          ))}
                                        </div>
                                      ))}
                                    </div>,
                                    document.body
                                  )}
                              </div>
                            </div>

                            {/* Swap Locations Button - theme by default, outline on hover */}
                            <div className="col-lg-auto col-md-6 book-form1-col d-flex justify-content-center align-items-end" style={{ paddingTop: "30px", paddingLeft: "8px", paddingRight: "8px" }}>
                              <button
                                type="button"
                                className="btn rounded-circle border"
                                onClick={handleSwapLocations}
                                onMouseEnter={() => setSwapBtnHover(true)}
                                onMouseLeave={() => setSwapBtnHover(false)}
                                style={{
                                  width: "42px",
                                  height: "42px",
                                  padding: 0,
                                  marginBottom: "0px",
                                  backgroundColor: swapBtnHover ? "#fff" : "#d70015",
                                  borderColor: "#d70015",
                                  color: swapBtnHover ? "#d70015" : "#fff",
                                }}
                                aria-label="Swap Pick Up and Drop Off"
                              >
                                <i className="fa fa-exchange-alt" />
                              </button>
                            </div>

                            {/* Drop Off - narrower when Round Trip to fit Return Date */}
                            <div className="col-md-6 book-form1-col" style={{ flex: tripType === "Round" ? "0 0 22%" : "0 0 26%", maxWidth: tripType === "Round" ? "22%" : "26%", minWidth: 0 }}>
                              <div className="form-group position-relative mb-0" style={{ zIndex: 1050 }}>
                                <label className="theme fw-bold mb-1 d-block" style={{ fontSize: "1rem" }}>Drop Off</label>
                                <input
                                  ref={dropOffInputRef}
                                  type="text"
                                  name="dropOff"
                                  value={formData.dropOff}
                                  onChange={handleInputChange}
                                  className="form-control border"
                                  placeholder="Enter drop off location"
                                  style={{ borderRadius: "4px", padding: "7px 13px", fontSize: "1rem", height: "42px", boxSizing: "border-box", width: "100%" }}
                                  autoComplete="off"
                                  required
                                />
                                {formData.dropOff && (
                                  <button
                                    type="button"
                                    className="theme close-circle"
                                    onClick={() => handleClearLocation("dropOff")}
                                    style={{
                                      position: "absolute",
                                      right: "3px",
                                      top: "calc(60% + 10px)",
                                      transform: "translateY(-50%)",
                                      border: "none",
                                      background: "transparent",
                                      padding: 0,
                                      color: "#777",
                                      cursor: "pointer",
                                    }}
                                    aria-label="Clear drop off location"
                                  >
                                    <i
                                      className="fa fa-times-circle"
                                      style={{ fontSize: "18px" }}
                                    />
                                  </button>
                                )}
                                {googleLoaded && dropOffSuggestions.length > 0 && dropOffDropdownRect &&
                                  createPortal(
                                    <div
                                      className="places-suggestion-list places-suggestion-dropdown"
                                      style={{
                                        position: "fixed",
                                        top: dropOffDropdownRect.top,
                                        left: dropOffDropdownRect.left,
                                        width: dropOffDropdownRect.width,
                                        zIndex: 99999,
                                        maxHeight: "280px",
                                        overflowY: "auto",
                                        backgroundColor: "#fff",
                                        margin: 0,
                                        borderRadius: "8px",
                                        boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                                        border: "1px solid #e8e8e8",
                                        padding: "6px 0",
                                      }}
                                    >
                                      {groupSuggestionsByCategory(dropOffSuggestions).map(({ category, items }) => (
                                        <div key={category} className="places-suggestion-group">
                                          <div className="places-suggestion-group-header">
                                            <i className={category === "Airport" ? "fa fa-plane" : category === "Terminal" ? "fa fa-building" : category === "Place" ? "fa fa-map-marker-alt" : "fa fa-map-pin"} />
                                            <span>{category}</span>
                                          </div>
                                          {items.map((s) => (
                                            <div
                                              key={s.placeId}
                                              className="places-suggestion-item"
                                              onClick={() => handleSelectSuggestion("dropOff", s)}
                                            >
                                              <span className="places-suggestion-main">{s.mainText || s.description}</span>
                                              {s.secondaryText && <span className="places-suggestion-secondary">{s.secondaryText}</span>}
                                            </div>
                                          ))}
                                        </div>
                                      ))}
                                    </div>,
                                    document.body
                                  )}
                              </div>
                            </div>

                            {/* Pickup Date & Time - narrower when Round Trip */}
                            <div className="col-md-6 book-form1-col" style={{ flex: tripType === "Round" ? "0 0 15%" : "0 0 18%", maxWidth: tripType === "Round" ? "15%" : "18%", minWidth: 0 }}>
                              <div className="form-group mb-0">
                                <label className="theme fw-bold mb-1 d-block" style={{ fontSize: "1rem" }}>Pickup Date</label>
                                <input
                                  ref={flightArrivalInputRef}
                                  type="text"
                                  name="flightArrival"
                                  readOnly
                                  value={formatDisplayDateTime(formData.flightArrival)}
                                  onClick={() => {
                                    setPickupTempDate(getDatePart(formData.flightArrival));
                                    setPickupTempTime(getTimePart(formData.flightArrival));
                                    setShowPickupDateTimePicker(true);
                                  }}
                                  className="form-control"
                                  placeholder="Select date"
                                  style={{
                                    borderRadius: "4px",
                                    padding: "7px 13px",
                                    fontSize: "1rem",
                                    height: "42px",
                                    boxSizing: "border-box",
                                    cursor: "pointer",
                                    backgroundColor: "#ffffff",
                                    border: "1px solid #d70015",
                                    width: "100%",
                                  }}
                                  required
                                />
                              </div>
                            </div>

                            {/* Return Date & Time (enabled only for Round Trip, single field with popup) */}
                            {tripType === "Round" && (
                              <div className="col-md-6 book-form1-col" style={{ flex: "0 0 15%", maxWidth: "15%", minWidth: 0 }}>
                                <div className="form-group mb-0">
                                  <label className="theme fw-bold mb-1 d-block" style={{ fontSize: "1rem" }}>Return Date</label>
                                  <input
                                    ref={returnDateInputRef}
                                    type="text"
                                    name="returnDate"
                                    readOnly
                                    value={formatDisplayDateTime(formData.returnDate)}
                                    onClick={() => {
                                      setReturnTempDate(getDatePart(formData.returnDate));
                                      setReturnTempTime(getTimePart(formData.returnDate));
                                      setShowReturnDateTimePicker(true);
                                    }}
                                    className="form-control border"
                                    placeholder="Select date"
                                    style={{
                                      borderRadius: "4px",
                                      padding: "7px 13px",
                                      fontSize: "1rem",
                                      height: "42px",
                                      backgroundColor: "#ffffff",
                                      boxSizing: "border-box",
                                      cursor: "pointer",
                                      width: "100%",
                                    }}
                                    required
                                  />
                                </div>
                              </div>
                            )}

                            {/* Search Button */}
                            <div className="col-lg-1 col-md-6 book-form1-col">
                              <div className="form-group mb-0 pt-4">
                                <button
                                  type="button"
                                  className="nir-btn w-50 rounded overflow-visible"
                                  style={{ fontSize: "1rem", fontWeight: "600", height: "42px", padding: "7px 30px" }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    //console.log("Search button clicked");
                                    
                                    // Get the form element
                                    const form = e.target.closest('form');
                                    if (form) {
                                      // Check form validity
                                      if (form.checkValidity()) {
                                        handleFormSubmit(e);
                                      } else {
                                        // Trigger browser validation
                                        form.reportValidity();
                                      }
                                    } else {
                                      // Fallback: call handler directly
                                      handleFormSubmit(e);
                                    }
                                  }}
                                >
                                  <i className="fa fa-search mr-2" style={{marginLeft: "-8px"}} /> {/*Search*/}
                                </button>
                              </div>
                            </div>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="white-overlay" />
        </section>
        {/* about-us starts */}
        <section className="about-us pb-6 bg-grey">
          <div className="container">
            <div className="section-title mb-6 w-50 text-lg-left text-center">
              <h4 className="mb-1 theme1">Core Features</h4>
              <h2 className="mb-0">
                Why Choose <span className="theme">Aircabi?</span>
              </h2>
            </div>
            {/* why us starts */}
            <div className="why-us">
              <div className="why-us-box">
                <div className="row">
                  <div className="col-lg-3 col-md-6 col-sm-6 mb-4">
                    <div className="why-us-item p-5 pt-6 pb-6 border rounded bg-white">
                      <div className="why-us-content">
                        <div className="why-us-icon mb-1">
                          <i className="icon-flag theme" />
                        </div>
                        <h4>
                          <Link to="/about">Flight-Based Scheduling</Link>
                        </h4>
                        <p className="mb-2">
                          We track your flight in real time and adjust pickup times accordingly—no extra charges.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-3 col-md-6 col-sm-6 mb-4">
                    <div className="why-us-item p-5 pt-6 pb-6 border rounded bg-white">
                      <div className="why-us-content">
                        <div className="why-us-icon mb-1">
                          <i className="icon-location-pin theme" />
                        </div>
                        <h4>
                          <Link to="/about">Reliable Airport Cabs</Link>
                        </h4>
                        <p className="mb-2">
                          Professional, verified drivers and well-maintained vehicles for a safe and comfortable journey.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-3 col-md-6 col-sm-6 mb-4">
                    <div className="why-us-item p-5 pt-6 pb-6 border rounded bg-white">
                      <div className="why-us-content">
                        <div className="why-us-icon mb-1">
                          <i className="icon-directions theme" />
                        </div>
                        <h4>
                          <Link to="/about">Always On Time</Link>
                        </h4>
                        <p className="mb-2">
                          Punctual pickups and smooth drop-offs with comfort, whether it’s an early departure or a midnight arrival.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-3 col-md-6 col-sm-6 mb-4">
                    <div className="why-us-item p-5 pt-6 pb-6 border rounded bg-white">
                      <div className="why-us-content">
                        <div className="why-us-icon mb-1">
                          <i className="icon-compass theme" />
                        </div>
                        <h4>
                          <Link to="/about">Transparent Pricing</Link>
                        </h4>
                        <p className="mb-2">
                          Know your fare upfront. No hidden charges. No last-minute surprises. No Hidden Charges.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* why us ends */}
          </div>
          <div className="white-overlay" />
        </section>
        {/* about-us ends */}
        {/* about-us starts */}
        <section
          className="about-us pb-20"
          style={{ backgroundImage: "url(images/bg/bg-trans.png)" }}
        >
          <div className="container">
            <div className="about-image-box">
              <div className="row d-flex align-items-center justify-content-between">
                <div className="col-lg-5 mb-4 pe-lg-4">
                  <div className="about-image1 overflow-hidden rounded">
                    <img src="images/destination/destination6.jpeg" alt="" />
                  </div>
                </div>
                <div className="col-lg-7 ps-lg-4">
                  <div className="about-content text-center text-lg-left">
                    <h4 className="theme d-inline-block mb-0">
                      About Aircabi
                    </h4>
                    <h2 className="border-b mb-2 pb-1">
                      Travel Smarter with Aircabi
                    </h2>
                    <p className="border-b mb-2 pb-2">
                      Aircabi is a smart airport cab booking platform designed for travelers who value comfort, reliability, and peace of mind. Whether you’re landing late at night, flying out early morning, or traveling for business or leisure, Aircabi ensures a smooth door-to-door airport transfer—every time.
                    </p>
                    <div className="about-listing">
                      <ul className="d-flex justify-content-between">
                        <li>
                          <i className="icon-location-pin theme" /> Reliable Services
                        </li>
                        <li>
                          <i className="icon-briefcase theme" /> Friendly Price
                        </li>
                        <li>
                          <i className="icon-folder theme" /> Flight Based Scheduling
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="white-overlay" />
        </section>
        {/* about-us ends */}
        {/* Counter - commented out; change false to true above to restore */}
        {false && (
        <section className="counter-main pt-0 mt-minus">
          <div className="container">
            <div className="counter p-4 pb-0 box-shadow bg-white rounded m-0">
              <div className="row">
                <div className="col-lg-3 col-md-6 col-sm-6 mb-4">
                  <div className="counter-item border-end pe-4 d-flex align-items-center text-start">
                    <i className="icon-clock bg-theme p-3 rounded me-3 white fs-4" />
                    <div className="counter-content">
                      <h2 className="value mb-0">20</h2>
                      <span className="m-0">Years Experiences</span>
                    </div>
                  </div>
                </div>
                <div className="col-lg-3 col-md-6 col-sm-6 mb-4">
                  <div className="counter-item border-end pe-4 d-flex align-items-center text-start">
                    <i className="icon-magnifier bg-theme p-3 rounded me-3 white fs-4" />
                    <div className="counter-content">
                      <h2 className="value mb-0">530</h2>
                      <span className="m-0">Tour Packages</span>
                    </div>
                  </div>
                </div>
                <div className="col-lg-3 col-md-6 col-sm-6 mb-4">
                  <div className="counter-item border-end pe-4 d-flex align-items-center text-start">
                    <i className="icon-user-following bg-theme p-3 rounded me-3 white fs-4" />
                    <div className="counter-content">
                      <h2 className="value mb-0">850</h2>
                      <span className="m-0">Happy Customers</span>
                    </div>
                  </div>
                </div>
                <div className="col-lg-3 col-md-6 col-sm-6 mb-4">
                  <div className="counter-item d-flex align-items-center text-start">
                    <i className="icon-trophy bg-theme p-3 rounded me-3 white fs-4" />
                    <div className="counter-content">
                      <h2 className="value mb-0">320</h2>
                      <span className="m-0">Award Winning</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        )}
        {/* End Counter */}

        {/* Last Minute Deal Starts */}
        <section
          className="trending pb-6 pt-6"
          style={{ backgroundImage: "url(images/shape2.png)" }}
        >
          <div className="container">
            <div className="section-title mb-6 w-75 mx-auto text-center">

              <h2 className="mb-1">
                  Most Booked <span className="theme">Airport Routes</span>
              </h2>

            </div>
            <div className="trend-box">
              <div className="row">
                <div className="col-12">
                  <div className="row">
                    {airportRoutes.map((route) => (
                      <div key={route.id} className="col-lg-4 col-md-6 mb-4">
                        <div className="trend-item rounded box-shadow">
                          <div className="trend-image position-relative">
                            <img
                              src={`images/trending/${route.img}`}
                              alt={`${route.from} to ${route.to}`}
                              className=""
                            />
                            <div className="color-overlay" />
                          </div>
                          <div className="trend-content p-4 pt-5 position-relative bg-white">
                            <div className="trend-meta bg-theme white px-3 py-2 rounded">
                              <Link to="/new_booking" className="white text-decoration-none fw-bold">
                                Book Now
                              </Link>
                            </div>
                            <h3 className="mb-2 mt-2">
                              <span className="theme">{route.from}</span>
                              {" "}
                              <span className="text-muted">→</span>
                              {" "}
                              {route.to}
                            </h3>
                            <p className="border-b pb-2 mb-2 text-muted small">
                              Fixed price airport transfer. Reliable cab service with meet &amp; greet.
                            </p>
                            <div className="entry-meta">
                              <div className="entry-author d-flex align-items-center">
                                <p className="mb-0">
                                  <span className="theme fw-bold fs-5">From £{route.fromPrice}</span>
                                  {" "}
                                  <span className="text-muted">one way</span>
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* Last Minute Deal Ends */}

        {/* our teams starts - vehicle types slider */}
        <section className="our-team pb-6">
          <div className="container">
            <div className="section-title mb-6 w-75 mx-auto text-center">
              <h4 className="mb-1 theme1">Our Vehicles</h4>
              <h2 className="mb-1">
                Choose Your <span className="theme">Cab Type</span>
              </h2>
              <p>
                From compact to VIP, select the right vehicle for your airport transfer.
              </p>
            </div>
            <div className="team-main">
              <div className="row shop-slider" ref={shopSliderRef}>
                <div className="col-lg-3 col-md-6 col-sm-12 mb-4">
                  <div className="team-list rounded">
                    <div className="team-image">
                      <img src="images/trending/aircabi_mini.png" alt="Small" />
                    </div>
                    <div className="team-content text-center p-3 bg-theme">
                      <h4 className="mb-0 white">Small</h4>
                      <p className="mb-0 white">Compact cab</p>
                    </div>
                  </div>
                </div>

                  <div className="col-lg-3 col-md-6 col-sm-12 mb-4">
                      <div className="team-list rounded">
                          <div className="team-image">
                              <img src="images/trending/aircabi_Large.png" alt="Small" />
                          </div>
                          <div className="team-content text-center p-3 bg-theme">
                              <h4 className="mb-0 white">Large</h4>
                              <p className="mb-0 white">Large cab</p>
                          </div>
                      </div>
                  </div>
                <div className="col-lg-3 col-md-6 col-sm-12 mb-4">
                  <div className="team-list rounded">
                    <div className="team-image">
                      <img src="images/trending/aircabi_xl.png" alt="Extra Large" />
                    </div>
                    <div className="team-content text-center p-3 bg-theme">
                      <h4 className="mb-0 white">Extra Large</h4>
                      <p className="mb-0 white">Spacious ride</p>
                    </div>
                  </div>
                </div>
                <div className="col-lg-3 col-md-6 col-sm-12 mb-4">
                  <div className="team-list rounded">
                    <div className="team-image">
                      <img src="images/trending/aircabi_xxl.png" alt="8 Seater" />
                    </div>
                    <div className="team-content text-center p-3 bg-theme">
                      <h4 className="mb-0 white">8 Seater</h4>
                      <p className="mb-0 white">Maximum space</p>
                    </div>
                  </div>
                </div>
                <div className="col-lg-3 col-md-6 col-sm-12 mb-4">
                  <div className="team-list rounded">
                    <div className="team-image">
                      <img src="images/trending/aircabi_vip.png" alt="VIP" />
                    </div>
                    <div className="team-content text-center p-3 bg-theme">
                      <h4 className="mb-0 white">VIP</h4>
                      <p className="mb-0 white">Premium service</p>
                    </div>
                  </div>
                </div>
                <div className="col-lg-3 col-md-6 col-sm-12 mb-4">
                  <div className="team-list rounded">
                    <div className="team-image">
                      <img src="images/trending/aircabi_van.png" alt="16 Seater" />
                    </div>
                    <div className="team-content text-center p-3 bg-theme">
                      <h4 className="mb-0 white">16 Seater</h4>
                      <p className="mb-0 white">Group travel</p>
                    </div>
                  </div>
                </div>
                <div className="col-lg-3 col-md-6 col-sm-12 mb-4">
                  <div className="team-list rounded">
                    <div className="team-image">
                      <img src="images/trending/aircabi_buss.png" alt="Mini Bus" />
                    </div>
                    <div className="team-content text-center p-3 bg-theme">
                      <h4 className="mb-0 white">Mini Bus</h4>
                      <p className="mb-0 white">Larger groups</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* our teams Ends */}

        {/* testomonial start */}
        <section
          className="testimonial pt-9 pb-6"
          style={{ backgroundImage: "url(images/testimonial.png)" }}
        >
          <div className="container">
            <div className="section-title mb-6 text-center w-75 mx-auto">
              <h4 className="mb-1 theme1">Our Testimonails</h4>
              <h2 className="mb-1">
                Good Reviews By <span className="theme">Clients</span>
              </h2>
              <p>
                Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do
                eiusmod tempor incididunt ut labore.
              </p>
            </div>
            <div className="row align-items-center">
              <div className="col-lg-5 pe-lg-4 mb-4">
                <div className="testimonial-image">
                  <img src="images/travel2.png" alt="" />
                </div>
              </div>
              <div className="col-lg-7 ps-lg-4 mb-4">
                <div className="row review-slider">
                  <div className="col-sm-4 item">
                    <div className="testimonial-item1 rounded">
                      <div className="author-info d-flex align-items-center mb-4">
                        <a href="#">
                          <img src="images/testimonial/img1.jpg" alt="" />
                        </a>
                        <div className="author-title ms-3">
                          <h5 className="m-0 theme">Jared Erondu</h5>
                          <span>Supervisor</span>
                        </div>
                      </div>
                      <div className="details">
                        <p className="m-0">
                          <i className="fa fa-quote-left me-2 fs-1" />
                          Lorem Ipsum is simply dummy text of the printing
                          andypesetting industry. Lorem ipsum a simple Lorem
                          Ipsum has been the industry's standard dummy hic et
                          quidem. Dignissimos maxime velit unde inventore quasi
                          vero dolorem.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="col-sm-4 item">
                    <div className="testimonial-item1 rounded">
                      <div className="author-info d-flex align-items-center mb-4">
                        <a href="#">
                          <img src="images/testimonial/img1.jpg" alt="" />
                        </a>
                        <div className="author-title ms-3">
                          <h5 className="m-0 theme">Jared Erondu</h5>
                          <span>Supervisor</span>
                        </div>
                      </div>
                      <div className="details">
                        <p className="m-0">
                          <i className="fa fa-quote-left me-2 fs-1" />
                          Lorem Ipsum is simply dummy text of the printing
                          andypesetting industry. Lorem ipsum a simple Lorem
                          Ipsum has been the industry's standard dummy hic et
                          quidem. Dignissimos maxime velit unde inventore quasi
                          vero dolorem.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* testimonial ends */}
      </>

      {/* Passenger and Luggage Selection Modal */}
      {showPassengerModal && (
        <div
          className="position-fixed"
          style={{
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
          onClick={() => setShowPassengerModal(false)}
        >
          <div
            className="bg-white rounded shadow-lg p-4"
            style={{
              maxWidth: "500px",
              width: "100%",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4 className="theme mb-0">Select Passengers & Luggage</h4>
              <button
                type="button"
                className="btn-close"
                onClick={() => setShowPassengerModal(false)}
                aria-label="Close"
              />
            </div>

            {/* Passengers Section */}
            <div className="mb-4">
             
              
              {/* Adults */}
              <div className="d-flex justify-content-between align-items-center mb-3 pb-3 border-bottom">
                <div>
                  <div className="fw-bold">Passengers</div>
                  
                </div>
                <div className="d-flex align-items-center gap-3">
                  <button
                    type="button"
                    className="btn btn-outline-secondary rounded-circle"
                    style={{ width: "35px", height: "35px", padding: 0 }}
                    onClick={() => handlePassengerChange("adults", "decrement")}
                    disabled={passengers.adults <= 1}
                  >
                    <i className="fa fa-minus"></i>
                  </button>
                  <span className="fw-bold" style={{ minWidth: "30px", textAlign: "center" }}>
                    {passengers.adults}
                  </span>
                  <button
                    type="button"
                    className="btn btn-outline-secondary rounded-circle"
                    style={{ width: "35px", height: "35px", padding: 0 }}
                    onClick={() => handlePassengerChange("adults", "increment")}
                  >
                    <i className="fa fa-plus"></i>
                  </button>
                </div>
              </div>
            </div>

            {/* Luggage Section (includes Medium/Large Luggage and Child Seat) */}
            <div className="mb-4">
              <h5 className="mb-3">Luggage</h5>
              
              {/* Medium Luggage */}
              <div className="d-flex justify-content-between align-items-center mb-3 pb-3 border-bottom">
                <div>
                  <div className="fw-bold">Medium Luggage</div>
                </div>
                <div className="d-flex align-items-center gap-3">
                  <button
                    type="button"
                    className="btn btn-outline-secondary rounded-circle"
                    style={{ width: "35px", height: "35px", padding: 0 }}
                    onClick={() => handleLuggageChange("medium", "decrement")}
                    disabled={luggage.medium <= 0}
                  >
                    <i className="fa fa-minus"></i>
                  </button>
                  <span className="fw-bold" style={{ minWidth: "30px", textAlign: "center" }}>
                    {luggage.medium}
                  </span>
                  <button
                    type="button"
                    className="btn btn-outline-secondary rounded-circle"
                    style={{ width: "35px", height: "35px", padding: 0 }}
                    onClick={() => handleLuggageChange("medium", "increment")}
                  >
                    <i className="fa fa-plus"></i>
                  </button>
                </div>
              </div>

              {/* Large Luggage */}
              <div className="d-flex justify-content-between align-items-center mb-3 pb-3 border-bottom">
                <div>
                  <div className="fw-bold">Large Luggage</div>

                </div>
                <div className="d-flex align-items-center gap-3">
                  <button
                    type="button"
                    className="btn btn-outline-secondary rounded-circle"
                    style={{ width: "35px", height: "35px", padding: 0 }}
                    onClick={() => handleLuggageChange("large", "decrement")}
                    disabled={luggage.large <= 0}
                  >
                    <i className="fa fa-minus"></i>
                  </button>
                  <span className="fw-bold" style={{ minWidth: "30px", textAlign: "center" }}>
                    {luggage.large}
                  </span>
                  <button
                    type="button"
                    className="btn btn-outline-secondary rounded-circle"
                    style={{ width: "35px", height: "35px", padding: 0 }}
                    onClick={() => handleLuggageChange("large", "increment")}
                  >
                    <i className="fa fa-plus"></i>
                  </button>
                </div>
              </div>

              {/* Child Seat */}
              <div className="d-flex justify-content-between align-items-center mb-3 pb-3 border-bottom">
                <div>
                  <div className="fw-bold">Child Seat</div>
                </div>
                <div className="d-flex align-items-center gap-3">
                  <button
                    type="button"
                    className="btn btn-outline-secondary rounded-circle"
                    style={{ width: "35px", height: "35px", padding: 0 }}
                    onClick={() => handleCarSeatChange("decrement")}
                    disabled={carSeat <= 0}
                  >
                    <i className="fa fa-minus"></i>
                  </button>
                  <span className="fw-bold" style={{ minWidth: "30px", textAlign: "center" }}>
                    {carSeat}
                  </span>
                  <button
                    type="button"
                    className="btn btn-outline-secondary rounded-circle"
                    style={{ width: "35px", height: "35px", padding: 0 }}
                    onClick={() => handleCarSeatChange("increment")}
                  >
                    <i className="fa fa-plus"></i>
                  </button>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowPassengerModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handlePassengerModalSubmit}
                style={{ backgroundColor: "#d70015", borderColor: "#d70015" }}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pickup DateTime Picker Modal */}
      {showPickupDateTimePicker && (
        <div
          className="position-fixed"
          style={{
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 2100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
          onClick={() => setShowPickupDateTimePicker(false)}
        >
          <div
            className="bg-white rounded shadow-lg p-4"
            style={{
              maxWidth: "400px",
              width: "100%",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h5 className="mb-3" style={{ color: "#d70015" }}>Select Pickup Date &amp; Time</h5>
            <div className="d-flex gap-2 mb-3">
              <input
                type="date"
                value={pickupTempDate}
                onChange={(e) => setPickupTempDate(e.target.value)}
                className="form-control border"
              />
              <select
                value={pickupTempTime}
                onChange={(e) => setPickupTempTime(e.target.value)}
                className="form-control border"
              >
                <option value="">Select time</option>
                {generateTimeOptions().map((time) => (
                  <option key={time} value={time}>
                    {time}
                  </option>
                ))}
              </select>
            </div>
            <div className="d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowPickupDateTimePicker(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                style={{ backgroundColor: "#d70015", borderColor: "#d70015" }}
                onClick={() => {
                  if (!pickupTempDate || !pickupTempTime) {
                    return;
                  }
                  const now = new Date();
                  const pickupDateTime = buildDateFromParts(pickupTempDate, pickupTempTime);
                  if (!pickupDateTime || pickupDateTime < now) {
                    setValidationError("Pickup date/time cannot be in the past.");
                    setValidationErrorType("dates");
                    return;
                  }
                  handleDateTimeChange("flightArrival", pickupTempDate, pickupTempTime);
                  setShowPickupDateTimePicker(false);
                }}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Return DateTime Picker Modal */}
      {showReturnDateTimePicker && (
        <div
          className="position-fixed"
          style={{
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 2100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
          onClick={() => setShowReturnDateTimePicker(false)}
        >
          <div
            className="bg-white rounded shadow-lg p-4"
            style={{
              maxWidth: "400px",
              width: "100%",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h5 className="mb-3" style={{ color: "#d70015" }}>Select Return Date &amp; Time</h5>
            <div className="d-flex gap-2 mb-3">
              <input
                type="date"
                value={returnTempDate}
                onChange={(e) => setReturnTempDate(e.target.value)}
                className="form-control border"
              />
              <select
                value={returnTempTime}
                onChange={(e) => setReturnTempTime(e.target.value)}
                className="form-control border"
              >
                <option value="">Select time</option>
                {generateTimeOptions().map((time) => (
                  <option key={time} value={time}>
                    {time}
                  </option>
                ))}
              </select>
            </div>
            <div className="d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowReturnDateTimePicker(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                style={{ backgroundColor: "#d70015", borderColor: "#d70015" }}
                onClick={() => {
                  if (!returnTempDate || !returnTempTime) {
                    return;
                  }
                  const now = new Date();
                  const returnDateTime = buildDateFromParts(returnTempDate, returnTempTime);
                  const pickupDateTime = formData.flightArrival
                    ? new Date(formData.flightArrival)
                    : null;
                  if (!returnDateTime || returnDateTime < now) {
                    setValidationError("Return date/time cannot be in the past.");
                    setValidationErrorType("dates");
                    return;
                  }
                  if (pickupDateTime && returnDateTime <= pickupDateTime) {
                    setValidationError("Return date/time must be after pickup date/time.");
                    setValidationErrorType("dates");
                    return;
                  }
                  handleDateTimeChange("returnDate", returnTempDate, returnTempTime);
                  setShowReturnDateTimePicker(false);
                }}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      <SeoLinksSection />
      <Footer />
    </>
  );
}
